import { useTranslation } from 'react-i18next';

import CreateAction from '~/components/Actions/CreateAction';
import BlueskyIcon from '~/components/Icons/BlueskyIcon';
import DiscordIcon from '~/components/Icons/DiscordIcon';
import FacebookIcon from '~/components/Icons/FacebookIcon';
import InstagramIcon from '~/components/Icons/InstagramIcon';
import LinkedinIcon from '~/components/Icons/LinkedInIcon';
import ThreadsIcon from '~/components/Icons/ThreadsIcon';
import TiktokIcon from '~/components/Icons/TiktokIcon';
import TwitterIcon from '~/components/Icons/TwitterIcon';
import YoutubeIcon from '~/components/Icons/YoutubeIcon';
import { Image } from '~/components/Image';
import { PoweredByBeehiiv } from '~/components/PoweredByBeehiiv';
import Text from '~/components/Text';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import type { Publication } from '~/interfaces/publication';
import type { Subscription } from '~/interfaces/subscriptions';
import makeExternalUrl from '~/utils/makeExternalUrl';

import AccountNavGroup from './AccountNavGroup';
import GeneralNavGroup from './GeneralNavGroup';
import NavGroups from './NavGroups';

interface Props {
  publication: Publication;
  isLoggedIn?: boolean;
  subscription?: Subscription;
}

const FooterLinks = ({ publication, isLoggedIn, subscription }: Props) => {
  const { web_template } = publication;
  const { nav_groups } = web_template;

  const isPremiumEnabled = publication.premium_enabled;
  const isReferralEnabled = publication.has_referral_program;
  const shouldShowAccountSection = isLoggedIn || isPremiumEnabled;
  const hasMultiplePublications = web_template?.multi_pub_ids?.length >= 1;
  const hasAuthorsPage = publication?.web_template?.author_ids?.length >= 1;

  return (
    <div>
      <div className="flex w-full">
        <div className="flex w-full justify-start space-x-6">
          {/* General Section */}
          <div className="flex flex-col">
            <GeneralNavGroup hasMultiplePublications={hasMultiplePublications} hasAuthorsPage={hasAuthorsPage} />
          </div>
          {/* Account Section */}
          {shouldShowAccountSection && (
            <AccountNavGroup
              isPremiumEnabled={isPremiumEnabled}
              isLoggedIn={isLoggedIn}
              isReferralEnabled={isReferralEnabled}
              publication={publication}
              subscription={subscription}
            />
          )}
          {/* Custom Links Section */}
          <div className="flex space-x-6">{nav_groups && <NavGroups navGroups={nav_groups} />}</div>
        </div>
      </div>
    </div>
  );
};

interface FooterProps {
  publication: Publication;
  subscription?: Subscription;
}

const SignupFooterv2 = ({ publication, subscription }: FooterProps) => {
  const isLoggedIn = useIsLoggedIn();
  const year = new Date().getFullYear();
  const { t } = useTranslation();
  const { web_template } = publication;
  const copyrightText = publication.copyright_text || publication.name;

  // Grab publication urls
  const tiktokUrl = publication.tiktok_url;
  const twitterUrl = publication.twitter_url;
  const youtubeUrl = publication.youtube_url;
  const facebookUrl = publication.facebook_url;
  const instagramUrl = publication.instagram_url;
  const linkedInUrl = publication.linkedin_url;
  const threadsUrl = publication.threads_url;
  const discordUrl = publication.discord_url;
  const blueskyUrl = publication.bluesky_url;

  const isPrivate = publication.private && !isLoggedIn;

  const renderSocialIcons = () => {
    return (
      <div className="flex flex-col space-y-1">
        <div className="flex space-x-2 transition-all">
          <div className="flex items-end space-x-2">
            {twitterUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(twitterUrl)} aria-label="X">
                <Text as="span" type="body" size="sm" theme="navs" weight="medium" className="text-md tracking-wide">
                  <TwitterIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
                </Text>
              </a>
            )}
            {linkedInUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(linkedInUrl)} aria-label="LinkedIn">
                <Text as="span" type="body" size="sm" theme="navs" weight="medium" className="text-md tracking-wide">
                  <LinkedinIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
                </Text>
              </a>
            )}
            {youtubeUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(youtubeUrl)} aria-label="Youtube">
                <Text as="span" type="body" size="sm" theme="navs" weight="medium" className="text-md tracking-wide">
                  <YoutubeIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
                </Text>
              </a>
            )}
            {tiktokUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(tiktokUrl)} aria-label="Tiktok">
                <Text as="span" type="body" size="sm" theme="navs" weight="medium" className="text-md tracking-wide">
                  <TiktokIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
                </Text>
              </a>
            )}
            {facebookUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(facebookUrl)} aria-label="Facebook">
                <Text as="span" type="body" size="sm" theme="navs" weight="medium" className="text-md tracking-wide">
                  <FacebookIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
                </Text>
              </a>
            )}
            {instagramUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(instagramUrl)} aria-label="Instagram">
                <Text as="span" type="body" size="sm" theme="navs" weight="medium" className="text-md tracking-wide">
                  <InstagramIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
                </Text>
              </a>
            )}
            {threadsUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(threadsUrl)} aria-label="Threads">
                <Text as="span" type="body" size="sm" theme="navs" weight="medium" className="text-md tracking-wide">
                  <ThreadsIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
                </Text>
              </a>
            )}
            {discordUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(discordUrl)} aria-label="Discord">
                <Text as="span" type="body" size="sm" theme="navs" weight="medium" className="text-md tracking-wide">
                  <DiscordIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
                </Text>
              </a>
            )}
            {blueskyUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(blueskyUrl)} aria-label="Bluesky">
                <Text as="span" type="body" size="sm" theme="navs" weight="medium" className="text-md tracking-wide">
                  <BlueskyIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
                </Text>
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <footer className="flex w-full flex-col bg-wt-tertiary px-4 py-10">
      <h2 id="footer-heading" className="sr-only">
        Footer
      </h2>
      <div className="mx-auto w-full max-w-6xl">
        <div className="flex flex-col-reverse justify-between space-x-0 md:flex-row md:space-x-8">
          <div className="w-full">
            <div className="w-full max-w-lg">
              <div className="flex space-x-4">
                <div>
                  <div className="h-10 w-10 overflow-hidden rounded">
                    {publication.logo.thumb.url && (
                      <Image
                        src={publication.logo.thumb.url}
                        alt={publication.name}
                        maxWidth={200}
                        aspectRatio="square"
                      />
                    )}
                  </div>
                </div>

                <div>
                  {publication.name && (
                    <Text as="h4" type="title" size="lg" theme="navs" weight="bold">
                      {publication.name}
                    </Text>
                  )}
                  {publication.description && (
                    <Text
                      as="p"
                      type="body"
                      size="sm"
                      theme="navs"
                      weight="medium"
                      className="text-md tracking-wide opacity-75"
                    >
                      {publication.description}
                    </Text>
                  )}
                </div>
              </div>

              <div className="w-full pt-4">
                {isLoggedIn && web_template.logged_subscribe_form ? (
                  <></>
                ) : (
                  <>{!isPrivate && <CreateAction publication={publication} />}</>
                )}
                <div className="mt-4 flex items-center">
                  <div>{renderSocialIcons()}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex hidden w-full flex-grow flex-col justify-between md:block">
            {!isPrivate && (
              <FooterLinks publication={publication} isLoggedIn={isLoggedIn} subscription={subscription} />
            )}
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center justify-between space-y-4 border-t border-white/20 pt-6 sm:flex-row sm:space-y-0">
          <div>
            <div className="flex flex-col items-center space-x-2 md:flex-row">
              <div className="flex items-center space-x-0 md:space-x-2">
                <Text as="span" theme="navs" className="extra-light text-xs">
                  {t('footer.copyright_simple', {
                    year: year,
                    name: copyrightText,
                  })}
                </Text>
              </div>
            </div>
          </div>
          <PoweredByBeehiiv publication={publication} />
        </div>
      </div>
    </footer>
  );
};

export default SignupFooterv2;
